import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRANCH_LABELS, type BranchCode } from '@lorsamthai/ops-shared';
import {
  createOrderDayByBranchPos,
  listOrderDayByBranch,
  runNightly,
  type OrderDayByBranchRow,
} from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtInt } from '../lib/format.js';
import { useAuth } from '../auth/context.js';
import { resolveSkuDisplay } from '../lib/skuParser.js';
import { formatCopyText } from '../lib/copyFormat.js';

interface RowState {
  checked: boolean;
  qty: number;
}

const NO_SUPPLIER_KEY = '__no_supplier__';
const rowKey = (r: { sku: string; dest_branch: BranchCode }) => `${r.dest_branch}::${r.sku}`;

export default function OrderDayByBranch() {
  const { me } = useAuth();
  const [rows, setRows] = useState<OrderDayByBranchRow[]>([]);
  const [state, setState] = useState<Record<string, RowState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [done, setDone] = useState<{ count: number; refs: string[] } | null>(null);
  // "transfer" = subtract bp's spare (order the residual). "direct" = ignore
  // bp, order each branch's own gap straight from the supplier.
  const [mode, setMode] = useState<'transfer' | 'direct'>('transfer');
  // Which branch to show; 'all' = every branch stacked (default).
  const [branchFilter, setBranchFilter] = useState<BranchCode | 'all'>('all');

  // Suggested supplier qty for a row under the active mode.
  const suggFor = (r: OrderDayByBranchRow, m: 'transfer' | 'direct') =>
    m === 'direct' ? r.suggested_qty_direct : r.suggested_qty;

  const buildState = (items: OrderDayByBranchRow[], m: 'transfer' | 'direct') => {
    const init: Record<string, RowState> = {};
    for (const row of items) {
      init[rowKey(row)] = {
        checked: row.supplier_id !== null && suggFor(row, m) > 0,
        qty: suggFor(row, m),
      };
    }
    return init;
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    setDone(null);
    try {
      const r = await listOrderDayByBranch();
      setRows(r.items);
      setState(buildState(r.items, mode));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  // Switching mode re-seeds qty/checked from the new mode's suggestion
  // (discards manual edits — a mode switch is a fresh proposal).
  const applyMode = (next: 'transfer' | 'direct') => {
    if (next === mode) return;
    setMode(next);
    setState(buildState(rows, next));
  };

  // The view returns every branch below its own min; each mode narrows that.
  // "transfer" reproduces the pre-012 gate exactly: hide a row whenever bp's
  // spare alone lifts dest back to min (suggested_qty > 0 is NOT equivalent —
  // the velocity top-up can stay positive even when bp covers the min gap).
  // "direct" shows every row it would actually order (qty > 0).
  // ใช้ effective_cy (DOT ปีปัจจุบัน) ให้ตรงกับ gate ของ view — ยางปีเก่าไม่นับ.
  const visibleRows = useMemo(
    () =>
      rows.filter((r) =>
        mode === 'transfer'
          ? r.effective_cy + r.transfer_credit < r.min_qty
          : r.suggested_qty_direct > 0
      ),
    [rows, mode]
  );

  const refresh = async () => {
    if (me?.role !== 'super_admin') {
      await load();
      return;
    }
    setRefreshing(true);
    setError(null);
    try {
      await runNightly();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  // 2-level grouping: branch → supplier → rows
  const groupedByBranch = useMemo(() => {
    type SupplierGroup = {
      supplier_id: number | null;
      supplier_name: string | null;
      rows: OrderDayByBranchRow[];
    };
    type BranchGroup = {
      branch: BranchCode;
      bySupplier: SupplierGroup[];
    };
    const branches = new Map<BranchCode, Map<string, SupplierGroup>>();
    for (const r of visibleRows) {
      const supKey = r.supplier_id === null ? NO_SUPPLIER_KEY : String(r.supplier_id);
      const branchMap = branches.get(r.dest_branch) ?? new Map<string, SupplierGroup>();
      const sup = branchMap.get(supKey) ?? {
        supplier_id: r.supplier_id,
        supplier_name: r.supplier_name,
        rows: [],
      };
      sup.rows.push(r);
      branchMap.set(supKey, sup);
      branches.set(r.dest_branch, branchMap);
    }
    const out: BranchGroup[] = [];
    for (const [branch, supMap] of branches.entries()) {
      out.push({
        branch,
        bySupplier: Array.from(supMap.values()),
      });
    }
    // Stable order: bp first, then ww, twn, llk
    const order: BranchCode[] = ['bp', 'ww', 'twn', 'llk'];
    out.sort((a, b) => order.indexOf(a.branch) - order.indexOf(b.branch));
    return out;
  }, [visibleRows]);

  // If the selected branch no longer has rows (mode switch, POs created),
  // fall back to showing all so the operator isn't stuck on an empty tab.
  useEffect(() => {
    if (
      branchFilter !== 'all' &&
      !groupedByBranch.some((g) => g.branch === branchFilter)
    ) {
      setBranchFilter('all');
    }
  }, [groupedByBranch, branchFilter]);

  const shownBranches =
    branchFilter === 'all'
      ? groupedByBranch
      : groupedByBranch.filter((g) => g.branch === branchFilter);

  const totals = useMemo(() => {
    let picked = 0;
    let qty = 0;
    const perBranch: Record<string, number> = {};
    for (const r of visibleRows) {
      const s = state[rowKey(r)];
      if (s?.checked) {
        picked += 1;
        qty += s.qty;
        perBranch[r.dest_branch] = (perBranch[r.dest_branch] ?? 0) + s.qty;
      }
    }
    return { picked, qty, perBranch };
  }, [visibleRows, state]);

  const setRow = (key: string, patch: Partial<RowState>) =>
    setState((p) => ({ ...p, [key]: { ...p[key]!, ...patch } }));

  const toggleSupplierGroup = (
    branch: BranchCode,
    supplierId: number | null,
    checked: boolean
  ) => {
    setState((p) => {
      const next = { ...p };
      for (const r of visibleRows) {
        if (r.dest_branch !== branch || r.supplier_id !== supplierId) continue;
        const k = rowKey(r);
        next[k] = {
          ...next[k]!,
          checked: checked && r.supplier_id !== null,
        };
      }
      return next;
    });
  };

  const copySupplierGroup = (branch: BranchCode, supplierId: number | null) => {
    const text = formatCopyText(
      visibleRows
        .filter((r) => r.dest_branch === branch && r.supplier_id === supplierId)
        .filter((r) => state[rowKey(r)]?.checked)
        .map((r) => ({
          sku: r.sku,
          brand: r.brand,
          model: r.model,
          size: r.size,
          qty: state[rowKey(r)]!.qty,
        }))
    );
    if (!text) return;
    void navigator.clipboard.writeText(text);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const items = visibleRows
        .filter((r) => state[rowKey(r)]?.checked && r.supplier_id !== null)
        .map((r) => ({
          sku: r.sku,
          qty: state[rowKey(r)]!.qty,
          supplier_id: r.supplier_id!,
          dest_branch: r.dest_branch,
        }));
      if (items.length === 0) {
        setError('ยังไม่ได้เลือกรายการที่จะสั่ง');
        setSubmitting(false);
        return;
      }
      const result = await createOrderDayByBranchPos(items);
      setDone({
        count: result.created.length,
        refs: result.created.map((c) => c.po_ref ?? `#${c.po_id}`),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">กำลังโหลด...</div>;

  const today = new Intl.DateTimeFormat('th-TH', { dateStyle: 'full' }).format(new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">สั่ง supplier แยกคลัง</h1>
          <p className="mt-1 text-sm text-slate-500">
            {today} · 1 PO ต่อ supplier ต่อสาขา (ส่งตรงถึงสาขา)
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            นับเฉพาะยาง DOT ปีปัจจุบัน — ของ DOT ปีเก่าไม่ถูกนับว่าครบ min/max
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing || loading}
          title="รัน run_nightly แล้วโหลดข้อมูลใหม่"
          className="shrink-0 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
        >
          {refreshing ? '⏳' : '🔄'} รีเฟรช
        </button>
      </div>

      {/* Mode toggle: subtract bp's transfer capacity vs order each branch direct */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
          <button
            onClick={() => applyMode('transfer')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              mode === 'transfer'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            🔁 โอนจาก bp ก่อน
          </button>
          <button
            onClick={() => applyMode('direct')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              mode === 'direct'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            🏪 สั่งตรงทุกสาขา
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {mode === 'transfer'
            ? 'หักของ DOT ปีปัจจุบันที่บางพลีมีเหลือ (โอนได้) ออกก่อน แล้วสั่ง supplier เฉพาะส่วนที่ขาด — สาขาที่ bp โอนคลุมได้จะไม่ขึ้น (ไปโอนที่หน้า "โอนวันนี้" แทน)'
            : 'ไม่หักของบางพลี — ทุกสาขาที่ยาง DOT ปีนี้ต่ำกว่า min จะสั่ง supplier ตามช่องว่างของตัวเองตรงๆ (รวมบางพลีเองด้วย)'}
        </p>
      </div>

      {done && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
          ✅ สร้าง PO {done.count} ใบแล้ว: {done.refs.join(', ')}
          <Link to="/pos" className="ml-2 underline">
            ไปดูใน PO list
          </Link>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {visibleRows.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <div className="text-4xl">🎉</div>
          <p className="mt-2 text-sm text-slate-600">
            {mode === 'transfer'
              ? 'ไม่มีอะไรต้องสั่งวันนี้ — ยาง DOT ปีนี้ทุกสาขาเกิน min หรือบางพลีโอนคลุมได้หมด'
              : 'ไม่มีอะไรต้องสั่งวันนี้ — ยาง DOT ปีนี้ทุกสาขาเกิน min ของตัวเอง'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
            <span className="font-semibold">{visibleRows.length}</span> SKU ใต้ min
            {' · '}
            <span className="font-semibold">{groupedByBranch.length}</span> สาขา
            {' · '}
            <span className="font-semibold">{totals.picked}</span> เลือกแล้ว
            (รวม <span className="font-semibold">{fmtInt(totals.qty)}</span> เส้น)
          </div>

          {/* Branch filter tabs — jump straight to a คลัง without scrolling. */}
          <div className="sticky top-0 z-10 -mx-1 flex flex-wrap gap-1.5 bg-slate-50/80 px-1 py-1 backdrop-blur">
            <button
              onClick={() => setBranchFilter('all')}
              className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                branchFilter === 'all'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              ทั้งหมด ({groupedByBranch.length})
            </button>
            {groupedByBranch.map(({ branch, bySupplier }) => {
              const count = bySupplier.reduce((s, g) => s + g.rows.length, 0);
              return (
                <button
                  key={branch}
                  onClick={() => setBranchFilter(branch)}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                    branchFilter === branch
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {BRANCH_LABELS[branch]} ({count})
                </button>
              );
            })}
          </div>

          {shownBranches.map(({ branch, bySupplier }) => {
            const branchTotal = totals.perBranch[branch] ?? 0;
            const branchRowCount = bySupplier.reduce((s, g) => s + g.rows.length, 0);
            return (
              <section
                key={branch}
                className="rounded-xl border border-slate-300 bg-slate-50/40"
              >
                <header className="flex items-center justify-between border-b border-slate-300 bg-slate-100 px-4 py-3">
                  <div>
                    <h2 className="text-lg font-bold">📦 {BRANCH_LABELS[branch]}</h2>
                    <div className="text-xs text-slate-600">
                      {branchRowCount} SKU · {bySupplier.length} supplier
                      {branchTotal > 0 && (
                        <> · เลือกรวม <span className="font-semibold">{fmtInt(branchTotal)}</span> เส้น</>
                      )}
                    </div>
                  </div>
                </header>

                <div className="space-y-3 p-3">
                  {bySupplier.map((sup) => {
                    const noSupplier = sup.supplier_id === null;
                    const allChecked = sup.rows.every((r) => state[rowKey(r)]?.checked);
                    const someChecked = sup.rows.some((r) => state[rowKey(r)]?.checked);
                    return (
                      <div
                        key={`${branch}::${sup.supplier_id ?? NO_SUPPLIER_KEY}`}
                        className="rounded-lg border border-slate-200 bg-white"
                      >
                        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
                          <div className="flex items-center gap-3">
                            {!noSupplier && (
                              <input
                                type="checkbox"
                                checked={allChecked}
                                ref={(el) => {
                                  if (el) el.indeterminate = !allChecked && someChecked;
                                }}
                                onChange={(e) =>
                                  toggleSupplierGroup(branch, sup.supplier_id, e.target.checked)
                                }
                              />
                            )}
                            <div>
                              <div className="text-sm font-semibold">
                                {sup.supplier_name ?? '⚠️ ยังไม่ระบุ supplier'}
                              </div>
                              <div className="text-xs text-slate-500">{sup.rows.length} SKU</div>
                            </div>
                          </div>
                          {!noSupplier && (
                            <button
                              onClick={() => copySupplierGroup(branch, sup.supplier_id)}
                              className="rounded-md border border-slate-200 px-3 py-1 text-xs hover:bg-slate-50"
                            >
                              📋 คัดลอก
                            </button>
                          )}
                        </div>
                        <ul className="divide-y divide-slate-100">
                          {sup.rows.map((r) => {
                            const k = rowKey(r);
                            const s = state[k] ?? { checked: false, qty: suggFor(r, mode) };
                            const dr = resolveSkuDisplay(r.sku, r.brand, r.model, r.size);
                            const display = [dr.brand, dr.model, dr.size]
                              .filter(Boolean)
                              .join(' ');
                            return (
                              <li
                                key={k}
                                className="flex flex-wrap items-center gap-3 px-4 py-2"
                              >
                                <input
                                  type="checkbox"
                                  checked={s.checked}
                                  disabled={noSupplier}
                                  onChange={(e) => setRow(k, { checked: e.target.checked })}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium">
                                    {display || (
                                      <span className="font-mono text-xs">{r.sku}</span>
                                    )}
                                  </div>
                                  <div className="mt-0.5 text-xs text-slate-500">
                                    <Link
                                      to={`/stock/${encodeURIComponent(r.sku)}`}
                                      className="font-mono hover:underline"
                                    >
                                      {r.sku}
                                    </Link>
                                    {' · '}
                                    เหลือ (DOT ปีนี้){' '}
                                    <span className="font-semibold">{r.effective_cy}</span> /
                                    min <span className="text-slate-600">{r.min_qty}</span> /
                                    max <span className="text-slate-600">{r.target_qty}</span>
                                    {r.incoming_po > 0 && (
                                      <> · <Badge tone="info">กำลังมา {r.incoming_po}</Badge></>
                                    )}
                                    {r.velocity > 0 && (
                                      <> · ขาย {r.velocity.toFixed(1)}/วัน</>
                                    )}
                                    {r.days_of_cover !== null && r.days_of_cover < 7 && (
                                      <>
                                        {' · '}
                                        <Badge tone={r.days_of_cover < 3 ? 'danger' : 'warn'}>
                                          เหลือ {r.days_of_cover.toFixed(0)} วัน
                                        </Badge>
                                      </>
                                    )}
                                  </div>
                                  {r.on_hand_old > 0 && (
                                    <div className="mt-0.5 text-xs text-amber-700">
                                      📦 มี DOT ปีเก่าค้าง {r.on_hand_old} เส้น — ไม่นับเป็นของที่ครบ
                                      min (รวมทุกปี {r.effective})
                                    </div>
                                  )}
                                  {mode === 'transfer' && r.transfer_credit > 0 && (
                                    <div className="mt-0.5 text-xs text-indigo-600">
                                      🔁 โอนจาก bp ได้ {r.transfer_credit} → สั่ง supplier แค่{' '}
                                      <span className="font-semibold">{r.suggested_qty}</span> เส้น
                                      <span className="text-slate-400">
                                        {' '}(ขาด {r.min_qty - r.effective_cy} ถึง min)
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min={1}
                                    value={s.qty}
                                    disabled={noSupplier}
                                    onChange={(e) =>
                                      setRow(k, { qty: Number(e.target.value) })
                                    }
                                    className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right text-sm disabled:bg-slate-50"
                                  />
                                  <span className="text-xs text-slate-500">เส้น</span>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                        {noSupplier && (
                          <div className="border-t border-slate-100 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                            💡 SKU ในกลุ่มนี้ยังไม่ระบุ supplier — เปิดหน้า SKU แล้วตั้งค่า
                            "Supplier หลัก"
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}

          {me?.role === 'super_admin' && (
            <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur sm:rounded-xl sm:border sm:bg-white">
              <button
                onClick={submit}
                disabled={submitting || totals.picked === 0}
                className="w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {submitting
                  ? 'กำลังบันทึก...'
                  : `บันทึกเป็น PO (${totals.picked} SKU · ${fmtInt(totals.qty)} เส้น)`}
              </button>
              <p className="mt-1 text-center text-xs text-slate-500">
                ระบบจะสร้าง 1 PO ต่อ (supplier × สาขา) — ส่งตรงถึงปลายทาง
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
